"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DeveloperModel = void 0;
const _ = require("lodash");
const ide_lib_reactor_data_layer_1 = require("@journeyapps-platform/ide-lib-reactor-data-layer");
const V5BaseModel_1 = require("./base/V5BaseModel");
const promises_1 = require("../utils/promises");
const CARDINAL_POLL_MS = 500;
const MAX_CARDINAL_USER_POLL_RETRIES = 4;
class DeveloperModel extends V5BaseModel_1.V5BaseModel {
    constructor(options) {
        super('user', options);
        this._loadPolicies = (0, promises_1.createMutexFunction)(async () => {
            const assignments = await this.loadCardinalUser();
            if (!assignments) {
                return;
            }
            /**
             * Need to load the policies and roles even if the array of policy ids
             * is empty (e.g in order to clear the collection after unassigning)
             */
            const promises = [];
            promises.push(this.policies.loadAll(() => {
                return this.client.listPolicies.paginate({
                    id: assignments.policy_ids || []
                });
            }));
            promises.push(this.roles.loadAll(() => {
                return this.client.listRoles.paginate({
                    id: assignments.role_ids || []
                });
            }));
            return Promise.all(promises);
        });
        this.policies = new ide_lib_reactor_data_layer_1.PaginatedCollection({
            transformer: (res) => {
                return res.items.map((p) => {
                    return {
                        developer: this,
                        policy: p
                    };
                });
            }
        });
        this.roles = new ide_lib_reactor_data_layer_1.PaginatedCollection({
            transformer: (res) => {
                return res.items.map((role) => {
                    return {
                        role: this.options.banks.roleBank.upsert(role),
                        developer: this
                    };
                });
            }
        });
        this.client = this.options.clients.provisionCardinalClient();
    }
    async getParents() {
        return [await this.getOrg()];
    }
    get org_id() {
        return this.model.org_id;
    }
    async unAssignRole(role) {
        await this.client.unassignRoleFromUser({
            user_id: this.id,
            role_id: role.id
        });
        this.loadPolicies();
    }
    async assignRole(role) {
        await this.client.assignRoleToUser({
            user_id: this.id,
            role_id: role.id
        });
        this.loadPolicies();
    }
    async assignPolicy(policy) {
        await this.client.assignPolicyToUser({
            user_id: this.id,
            policy_id: policy.id
        });
        this.loadPolicies();
    }
    async unAssignPolicy(id) {
        await this.client.unassignPolicyFromUser({
            user_id: this.id,
            policy_id: id
        });
        this.loadPolicies();
    }
    async loadCardinalUser() {
        for (let retryIndex = 0; retryIndex < MAX_CARDINAL_USER_POLL_RETRIES; retryIndex++) {
            try {
                return await this.client.getUserAssignments({ user_id: this.id });
            }
            catch (error) {
                console.warn(`User not found in Cardinal. Retry ${retryIndex}/${MAX_CARDINAL_USER_POLL_RETRIES}`);
            }
            await new Promise((resolve) => setTimeout(resolve, CARDINAL_POLL_MS));
        }
    }
    loadPolicies() {
        return this._loadPolicies();
    }
    async delete() {
        var _a;
        await this.options.clients.provisionAccountsV5Client().deleteUser({
            id: this.id
        });
        return (_a = this.org) === null || _a === void 0 ? void 0 : _a.loadDevelopers();
    }
    get org() {
        return this.options.banks.orgBank.org(this.options.model.org_id);
    }
    get email() {
        return this.options.model.email;
    }
    getOrg() {
        return this.options.banks.orgBank.resolve(this.options.model.org_id);
    }
    firstName() {
        var _a, _b;
        return ((_b = (_a = this.options.model.name) === null || _a === void 0 ? void 0 : _a.split(' ')) === null || _b === void 0 ? void 0 : _b[0]) || null;
    }
    lastName() {
        const name = this.options.model.name;
        if (!name) {
            return null;
        }
        const parts = name.split(' ');
        return _.takeRight(parts, parts.length - 1).join(' ');
    }
}
exports.DeveloperModel = DeveloperModel;
